import { c as _c } from "react/compiler-runtime";
import type { InterfaceToken } from '@morpho-org/dapp-core';
import { ZERO } from '@morpho-org/dapp-core';
import { formatters } from '@morpho-org/ui-v6/utils';
import { TooltipItem, TooltipLink } from '@repo/ui';
import { IN_KIND_REDEMPTION_HELP_URL } from '@/config/external-urls';
import { formatFeeAmount, formatTokenAmount } from '@/helpers/formatters';
interface InKindRedemptionPositionExplanationProps {
  vaultAsset: InterfaceToken;
  marketName: string;
  /** What leaves the Vault deposit. */
  withdrawnAssets: bigint;
  /** The redemption penalty charged on the Market leg. */
  penaltyAssets: bigint;
  /** What is supplied to the Market on the user's behalf. */
  marketAssets: bigint;
  /** The part of the exit the Vault's idle liquidity pays straight to the wallet. */
  idleAssets: bigint;
  /** Force-deallocate penalty rate (WAD). */
  penaltyRate: bigint;
}

/**
 * What the wallet leg is. Shared by the chooser breakdown and the recap so the two surfaces
 * cannot drift apart.
 */
export const WALLET_LEG_NOTE = "This Vault's available liquidity that can be withdrawn with no fee.";

/**
 * Once the receipt is decoded the leg is a settled payout, not current capacity.
 */
export const WALLET_LEG_REALISED_NOTE = "Paid from the Vault's available liquidity, with no fee.";

/**
 * The wallet leg is an estimate until the receipt lands, and it can only move one way.
 */
export const WALLET_LEG_CEILING_NOTE = 'If the available liquidity drops before your transaction confirms, more of your withdrawal ' + 'arrives as a Market position and the fee grows with it. The total you withdraw stays the same.';

/**
 * Why the Market leg is smaller than what left the Vault, as tooltip content (Figma
 * 20225:129355). QA read the fee as money taken out of a wallet payout that never arrived —
 * prose could not fix that, so the rows do the arithmetic in the open. They reconcile the whole
 * exit — `withdrawnAssets = idleAssets + penaltyAssets + marketAssets` — which is why the wallet
 * leg needs a row of its own whenever idle liquidity pays part of the redemption: rows that do
 * not add up would re-create the confusion this breakdown was built to end.
 */
const InKindRedemptionPositionExplanation = t0 => {
  const $ = _c(38);
  const {
    vaultAsset,
    marketName,
    withdrawnAssets,
    penaltyAssets,
    marketAssets,
    idleAssets,
    penaltyRate
  } = t0;
  let t1;
  if ($[0] !== vaultAsset) {
    t1 = amount => formatTokenAmount(vaultAsset, amount);
    $[0] = vaultAsset;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const format = t1;
  const hasWalletLeg = idleAssets > ZERO;
  let t2;
  if ($[2] !== hasWalletLeg || $[3] !== marketName || $[4] !== vaultAsset.symbol) {
    t2 = <p>{hasWalletLeg ? <>You receive part of your exit as {vaultAsset.symbol} in your wallet, and the rest as a lending position in {marketName}.</> : <>You receive a lending position in {marketName}, not {vaultAsset.symbol} in your wallet.</>}</p>;
    $[2] = hasWalletLeg;
    $[3] = marketName;
    $[4] = vaultAsset.symbol;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== format || $[7] !== withdrawnAssets) {
    t3 = format(withdrawnAssets);
    $[6] = format;
    $[7] = withdrawnAssets;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t3) {
    t4 = <TooltipItem label="Withdrawn from the Vault" value={t3} />;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== format || $[12] !== hasWalletLeg || $[13] !== idleAssets) {
    t5 = hasWalletLeg && <TooltipItem label="Paid to your wallet" value={`-${format(idleAssets)}`} />;
    $[11] = format;
    $[12] = hasWalletLeg;
    $[13] = idleAssets;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== penaltyRate) {
    t6 = formatters.percent.digits(2).unit("%").of(penaltyRate, 18);
    $[15] = penaltyRate;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const t7 = `Fee (${t6})`;
  let t8;
  if ($[17] !== penaltyAssets || $[18] !== vaultAsset.decimals || $[19] !== vaultAsset.price || $[20] !== vaultAsset.symbol) {
    t8 = formatFeeAmount({
      price: vaultAsset.price,
      amount: penaltyAssets,
      decimals: vaultAsset.decimals,
      withTrailingZero: false,
      symbol: vaultAsset.symbol
    });
    $[17] = penaltyAssets;
    $[18] = vaultAsset.decimals;
    $[19] = vaultAsset.price;
    $[20] = vaultAsset.symbol;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const t9 = `-${t8}`;
  let t10;
  if ($[22] !== t7 || $[23] !== t9) {
    t10 = <TooltipItem label={t7} value={t9} />;
    $[22] = t7;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== format || $[26] !== marketAssets) {
    t11 = format(marketAssets);
    $[25] = format;
    $[26] = marketAssets;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t11) {
    t12 = <TooltipItem label="Position you receive" value={t11} />;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  let t14;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <p className="mt-xs">The fee stays in the Vault, shared across the depositors who remain.</p>;
    t14 = <TooltipLink href={IN_KIND_REDEMPTION_HELP_URL} target="_blank" rel="noopener noreferrer">Learn about in-kind redemption →</TooltipLink>;
    $[30] = t13;
    $[31] = t14;
  } else {
    t13 = $[30];
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t10 || $[33] !== t12 || $[34] !== t2 || $[35] !== t4 || $[36] !== t5) {
    t15 = <>{t2}{t4}{t5}{t10}{t12}{t13}{t14}</>;
    $[32] = t10;
    $[33] = t12;
    $[34] = t2;
    $[35] = t4;
    $[36] = t5;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  return t15;
};
export default InKindRedemptionPositionExplanation;